import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Video,
  Loader2,
  Download,
  X,
  AlertCircle,
  CheckCircle,
  Settings2,
  FileJson,
  RefreshCw,
  Lock,
  Unlock,
  History,
  Trash2,
  Sparkles,
} from "lucide-react";
import bundledTemplate from "../lib/ltx-i2v.api.json";
import { ProductSourcePicker, type PickedSource } from "../components/video/ProductSourcePicker";
import { AttachVideoToDraft } from "../components/video/AttachVideoToDraft";
import { BulkVideoQueue } from "../components/video/BulkVideoQueue";
import { ComfyFileLink } from "../components/video/ComfyFileLink";
import { authHeaders, getBackendUrl } from "../lib/http";
import { useShopVertical } from "../lib/useShopVertical";
import { detectPreset } from "../lib/ltxDetect";
import {
  DEFAULT_COMFY_URL,
  buildPrompt,
  checkComfy,
  getComfyUrl,
  getOutputDir,
  setOutputDir,
  queuePrompt,
  setComfyUrl,
  uploadImage,
  watchJob,
  type ApiWorkflow,
  type ComfyStats,
  type Progress,
  type VideoOutput,
} from "../lib/comfyui";
import {
  ASPECTS,
  DURATIONS,
  JEWELRY_WISHES,
  PRODUCT_PRESETS,
  QUALITY_TIERS,
  dimensionsFor,
  framesFor,
  negativeFor,
  secondsOf,
  sceneTextFor,
} from "../lib/ltxPresets";

const TEMPLATE_KEY = "erc-ltx-workflow-template";
const WISH_KEY = "erc-ltx-jewelry-wish";
const HISTORY_KEY = "erc-ltx-history";
/** Son secilen uretim ayarlari (oran, kalite, sure, ses, netlik, baglilik) */
const SETTINGS_KEY = "erc-ltx-settings";

interface SavedSettings {
  aspectId?: string; qualityId?: string; seconds?: number; withAudio?: boolean;
  imgCompression?: number; strengthOverride?: number | null; variations?: number;
}
function loadSettings(): SavedSettings {
  try { return JSON.parse(localStorage.getItem(SETTINGS_KEY) || "{}") as SavedSettings; } catch { return {}; }
}
const FPS = 24;
const MAX_HISTORY = 30;

/** Panele gomulu, dogrulanmis LTX-2.3 gorselden-video sablonu. */
const DEFAULT_TEMPLATE = bundledTemplate as unknown as ApiWorkflow;

function loadTemplate(): ApiWorkflow {
  try {
    const raw = localStorage.getItem(TEMPLATE_KEY);
    return raw ? (JSON.parse(raw) as ApiWorkflow) : DEFAULT_TEMPLATE;
  } catch {
    return DEFAULT_TEMPLATE;
  }
}

/** API formati mi, yoksa yanlislikla arayuz formati mi verilmis anlar. */
function validateTemplate(data: unknown): { ok: true; wf: ApiWorkflow } | { ok: false; reason: string } {
  if (!data || typeof data !== "object") return { ok: false, reason: "Dosya JSON değil." };
  if ("nodes" in (data as any) && Array.isArray((data as any).nodes)) {
    return {
      ok: false,
      reason:
        "Bu arayüz formatı. ComfyUI'de Workflow menüsünden 'Export (API)' ile dışarı aktar, kaydettiğin dosyayı değil.",
    };
  }
  const wf = data as ApiWorkflow;
  const ids = Object.keys(wf);
  if (!ids.length || !ids.every((id) => wf[id] && typeof wf[id].class_type === "string")) {
    return { ok: false, reason: "API formatında bir workflow değil." };
  }
  const classes = new Set(ids.map((id) => wf[id].class_type));
  const missing = ["LoadImage", "EmptyLTXVLatentVideo"].filter((c) => !classes.has(c));
  if (missing.length) return { ok: false, reason: `Şablonda şu düğümler yok: ${missing.join(", ")}` };
  return { ok: true, wf };
}

interface HistoryItem {
  id: string;
  ts: number;
  filename: string;
  url: string;
  prompt: string;
  presetId: string;
  aspectId: string;
  seconds: number;
  strength: number;
  seed: number;
  width: number;
  height: number;
}

function loadHistory(): HistoryItem[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export default function VideoStudioPage() {
  const [template, setTemplate] = useState<ApiWorkflow>(loadTemplate);
  const [customTemplate, setCustomTemplate] = useState(() => {
    try {
      return localStorage.getItem(TEMPLATE_KEY) != null;
    } catch {
      return false;
    }
  });
  const [templateError, setTemplateError] = useState<string | null>(null);

  const [stats, setStats] = useState<ComfyStats | null>(null);
  const [connError, setConnError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [urlDraft, setUrlDraft] = useState(getComfyUrl());
  // ComfyUI cikti klasoru: elle girilen > ComfyUI'nin bildirdigi
  const [outputDirSaved, setOutputDirSaved] = useState(getOutputDir());
  const [outputDirDraft, setOutputDirDraft] = useState(getOutputDir());

  const [file, setFile] = useState<File | null>(null);
  const [sourceLabel, setSourceLabel] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);
  const [sourceBusy, setSourceBusy] = useState(false);
  const [sourceError, setSourceError] = useState<string | null>(null);
  const [prompt, setPrompt] = useState("");

  const { vertical, shop } = useShopVertical();
  const [detectNote, setDetectNote] = useState<string | null>(null);
  const [sceneFamilies, setSceneFamilies] = useState<Array<{ id: string; name: string }>>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisNote, setAnalysisNote] = useState<string | null>(null);
  const [analysisCached, setAnalysisCached] = useState(false);
  // Hizli art arda kaynak seciminde eski analizin yeni urunun alanlarini ezmemesi icin
  const analysisSeq = useRef(0);

  // Takida istenen video turu; secim tarayicida hatirlanir.
  const [wishId, setWishId] = useState<string>(() => {
    try {
      return localStorage.getItem(WISH_KEY) ?? JEWELRY_WISHES[0].id;
    } catch {
      return JEWELRY_WISHES[0].id;
    }
  });
  const [wishNote, setWishNote] = useState("");

  // Son ayarlar hatirlanir (tekli uretim ve toplu kuyruk ayni ayarlari kullanir)
  const [saved] = useState(loadSettings);
  const [presetId, setPresetId] = useState(PRODUCT_PRESETS[0].id);
  const [aspectId, setAspectId] = useState(saved.aspectId && ASPECTS.some((a) => a.id === saved.aspectId) ? saved.aspectId : ASPECTS[0].id);
  const [qualityId, setQualityId] = useState(saved.qualityId && QUALITY_TIERS.some((q) => q.id === saved.qualityId) ? saved.qualityId : QUALITY_TIERS[0].id);
  const [seconds, setSeconds] = useState<number>(saved.seconds ?? PRODUCT_PRESETS[0].seconds);
  const [variations, setVariations] = useState(saved.variations ?? 1);
  const [withAudio, setWithAudio] = useState(saved.withAudio ?? false);

  // Gelismis
  const [strengthOverride, setStrengthOverride] = useState<number | null>(saved.strengthOverride ?? null);
  const [imgCompression, setImgCompression] = useState(saved.imgCompression ?? 18);
  useEffect(() => {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify({ aspectId, qualityId, seconds, withAudio, imgCompression, strengthOverride, variations }));
    } catch { /* depolama kapali: ayar yalniz bu oturumda */ }
  }, [aspectId, qualityId, seconds, withAudio, imgCompression, strengthOverride, variations]);
  const [seed, setSeed] = useState<number>(() => Math.floor(Math.random() * 2 ** 31));
  const [seedLocked, setSeedLocked] = useState(false);
  const [negativeOverride, setNegativeOverride] = useState<string | null>(null);

  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [doneCount, setDoneCount] = useState(0);
  const [results, setResults] = useState<VideoOutput[]>([]);
  const [runError, setRunError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [history, setHistory] = useState<HistoryItem[]>(loadHistory);

  const abortRef = useRef<AbortController | null>(null);

  const preset = useMemo(
    () => PRODUCT_PRESETS.find((p) => p.id === presetId) ?? PRODUCT_PRESETS[0],
    [presetId],
  );
  const aspect = useMemo(() => ASPECTS.find((a) => a.id === aspectId) ?? ASPECTS[0], [aspectId]);
  const quality = useMemo(
    () => QUALITY_TIERS.find((q) => q.id === qualityId) ?? QUALITY_TIERS[0],
    [qualityId],
  );
  const dims = useMemo(() => dimensionsFor(aspect, quality.longEdge), [aspect, quality]);
  const frames = useMemo(() => framesFor(seconds, FPS), [seconds]);
  // Istek sadece taki on ayarinda gecerli; kapatilmis urun gibi on ayarlarda
  // hareketi kisitlamak istekten onemli.
  const wish = presetId === "taki" ? JEWELRY_WISHES.find((w) => w.id === wishId) ?? JEWELRY_WISHES[0] : null;
  // Hazir takı prompt'u hareket kurallarini zaten iceriyor; sabit ek tekrar olurdu
  const promptSuffix = wish ? "" : preset.promptSuffix;
  const strength = strengthOverride ?? wish?.strength ?? preset.strength;
  const negative = negativeOverride ?? negativeFor(preset);

  // Urun tipi degisince onerilen sureyi de getir; kullanici sonra elle degistirebilir.
  const pickPreset = (id: string) => {
    setPresetId(id);
    const p = PRODUCT_PRESETS.find((x) => x.id === id);
    if (p) setSeconds(p.seconds);
    setStrengthOverride(null);
    setNegativeOverride(null);
    if (id === "taki") fillWishScene(wishId);
  };

  /** AI ile yazdirirken gidecek istek metni; taki disinda bos. */
  const wishTextFor = (forPresetId: string) => {
    if (forPresetId !== "taki") return "";
    const w = JEWELRY_WISHES.find((x) => x.id === wishId) ?? JEWELRY_WISHES[0];
    return wishNote.trim() ? `${w.brief} Seller's extra request (may be Turkish): ${wishNote.trim()}` : w.brief;
  };

  // Hazir prompt'u sahne tarifine koyar; yoldaki AI analizi varsa sonucu yok sayilir.
  const fillWishScene = (id: string) => {
    const w = JEWELRY_WISHES.find((x) => x.id === id) ?? JEWELRY_WISHES[0];
    analysisSeq.current++;
    setAnalyzing(false);
    setAnalysisCached(false);
    setAnalysisNote(null);
    setPrompt(w.scene);
  };

  const applyWish = (id: string) => {
    setWishId(id);
    try {
      localStorage.setItem(WISH_KEY, id);
    } catch {
      /* tarayici depolamasi kapaliysa secim sadece bu oturumda kalir */
    }
    fillWishScene(id);
  };

  // Uzak gorseli API proxy'si uzerinden blob'a cevirir: etsystatic gibi
  // kaynaklar CORS basligi vermiyor, tarayici dogrudan fetch edemiyor.
  /** Secilen urunun taslaklari: "Taslaga ekle"de en ustte */
  const [sourceDraftIds, setSourceDraftIds] = useState<string[]>([]);
  const pickSource = async (src: PickedSource) => {
    setSourceError(null);
    setAnalysisNote(null);
    setAnalysisCached(false);
    setSourceLabel(src.label);
    setSourceDraftIds(src.kind === "remote" ? src.draftIds ?? [] : []);

    const det = detectPreset({ vertical, title: src.label });
    pickPreset(det.presetId);
    // Takida hazir prompt dolar, AI'a gidilmez; digerlerinde kayitli analiz kullanilir
    const autoAnalyze = det.presetId !== "taki";
    setDetectNote(`Ürün tipi ${det.reason} seçildi`);

    if (src.kind === "upload") {
      setFile(src.file);
      setSourceUrl(null);
      if (autoAnalyze) void analyzeImage({ file: src.file, url: null, label: src.label });
      return;
    }
    setSourceBusy(true);
    try {
      const res = await fetch(
        `${getBackendUrl()}/api/media/proxy-image?url=${encodeURIComponent(src.url)}`,
        { headers: authHeaders() },
      );
      if (!res.ok) throw new Error(`Görsel alınamadı (${res.status})`);
      const blob = await res.blob();
      const ext = (blob.type.split("/")[1] ?? "jpg").replace("jpeg", "jpg");
      setFile(new File([blob], `erc-${Date.now()}.${ext}`, { type: blob.type }));
      setSourceUrl(src.url);
      // Kayitli analiz varsa API onu dondurur; yoksa bir kez analiz edip saklar.
      if (autoAnalyze) void analyzeImage({ file: null, url: src.url, label: src.label });
    } catch (e) {
      setSourceError(e instanceof Error ? e.message : String(e));
      setFile(null);
    } finally {
      setSourceBusy(false);
    }
  };

  // Yerel dosyada URL yok; vision modeline gondermek icin kucultup data URL yap.
  const toDataUrl = (f: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const objUrl = URL.createObjectURL(f);
      img.onload = () => {
        URL.revokeObjectURL(objUrl);
        const max = 1024;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const c = document.createElement("canvas");
        c.width = Math.round(img.width * scale);
        c.height = Math.round(img.height * scale);
        const ctx = c.getContext("2d");
        if (!ctx) return reject(new Error("Canvas kullanılamadı"));
        ctx.drawImage(img, 0, 0, c.width, c.height);
        resolve(c.toDataURL("image/jpeg", 0.8));
      };
      img.onerror = () => {
        URL.revokeObjectURL(objUrl);
        reject(new Error("Görsel okunamadı"));
      };
      img.src = objUrl;
    });

  // Kaynak secilince otomatik cagrilir (API kayitli analizi dondurur);
  // buton ise refresh ile yeniden analiz ettirir. Parametreler state yerine
  // disaridan geliyor cunku pickSource icinde state henuz guncellenmemis olur.
  const analyzeImage = async (src: {
    file: File | null;
    url: string | null;
    label: string | null;
    wish?: string;
    refresh?: boolean;
  }) => {
    if (!src.file && !src.url) return;
    const seq = ++analysisSeq.current;
    setAnalyzing(true);
    setAnalysisNote(null);
    try {
      const imageUrl = src.url ?? (await toDataUrl(src.file!));
      const res = await fetch(`${getBackendUrl()}/api/media/describe-scene`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          imageUrl,
          productHint: src.label ?? undefined,
          wish: src.wish || undefined,
          refresh: src.refresh,
        }),
      });
      if (seq !== analysisSeq.current) return;
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        // Hata yakalayici gercek sebebi 'message' alaninda donuyor;
        // 'error' sadece kod ("INTERNAL_ERROR") oldugu icin yeterli degil.
        throw new Error(body.message || body.error || `Analiz başarısız (${res.status})`);
      }
      const { data } = await res.json();
      if (seq !== analysisSeq.current) return;
      setPrompt(data.scene);
      setAnalysisCached(Boolean(data.cached));
      // Model urunun ortuldugunu soylerse dogru on ayara gecmek kritik:
      // ortuk alan, kapatan nesne kalkinca uydurma nesne olarak geri geliyor.
      if (data.occluded) pickPreset("kapali");
      setAnalysisNote(data.notes || (data.occluded ? "Ürün kısmen örtülü, 'Kapatılmış ürün' ön ayarına geçildi." : null));
    } catch (e) {
      if (seq === analysisSeq.current) setAnalysisNote(e instanceof Error ? e.message : String(e));
    } finally {
      if (seq === analysisSeq.current) setAnalyzing(false);
    }
  };

  const ping = useCallback(async () => {
    setChecking(true);
    setConnError(null);
    try {
      setStats(await checkComfy());
    } catch {
      setStats(null);
      setConnError(
        "ComfyUI'ye bağlanılamadı. Yerel sunucunun çalıştığından ve CORS izni verildiğinden emin ol.",
      );
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    void ping();
  }, [ping]);

  useEffect(() => {
    fetch(`${getBackendUrl()}/api/media/scene-families`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => b?.data && setSceneFamilies(b.data))
      .catch(() => {
        /* sahne aileleri alinamazsa sadece serbest metin kalir */
      });
  }, []);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    if (!running) return;
    const t = window.setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => window.clearInterval(t);
  }, [running]);

  const pushHistory = (items: HistoryItem[]) => {
    setHistory((prev) => {
      const next = [...items, ...prev].slice(0, MAX_HISTORY);
      try {
        localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
      } catch {
        /* kota dolabilir; gecmis kritik degil */
      }
      return next;
    });
  };

  const onTemplateFile = async (f: File) => {
    setTemplateError(null);
    try {
      const parsed = JSON.parse(await f.text());
      const v = validateTemplate(parsed);
      if (!v.ok) return setTemplateError(v.reason);
      localStorage.setItem(TEMPLATE_KEY, JSON.stringify(v.wf));
      setTemplate(v.wf);
      setCustomTemplate(true);
    } catch {
      setTemplateError("Dosya okunamadı ya da geçerli JSON değil.");
    }
  };

  const generate = async () => {
    if (!file || !prompt.trim()) return;
    setRunning(true);
    setRunError(null);
    setResults([]);
    setDoneCount(0);
    setElapsed(0);
    setProgress({ ratio: null, label: "Görsel yükleniyor" });

    const ac = new AbortController();
    abortRef.current = ac;
    const baseSeed = seedLocked ? seed : Math.floor(Math.random() * 2 ** 31);
    if (!seedLocked) setSeed(baseSeed);

    const fullPrompt = `${prompt.trim()} ${promptSuffix}`.trim();

    try {
      const img = await uploadImage(file);

      // Hepsini once kuyruga at: ComfyUI sirayla isler, GPU bos kalmaz.
      const jobs = [];
      for (let i = 0; i < variations; i++) {
        const wf = buildPrompt(template, {
          imageName: img.name,
          prompt: fullPrompt,
          negativePrompt: negative,
          width: dims.width,
          height: dims.height,
          frames,
          seed: baseSeed + i * 1000,
          withAudio,
          strength,
          fps: FPS,
          imgCompression,
        });
        jobs.push(await queuePrompt(wf));
      }

      setProgress({ ratio: null, label: "Model yükleniyor (ilk çalıştırmada uzun sürer)" });

      const collected: VideoOutput[] = [];
      for (const [i, job] of jobs.entries()) {
        const outs = await watchJob(
          job,
          (p) => setProgress({ ...p, label: `${i + 1}/${jobs.length} · ${p.label}` }),
          ac.signal,
        );
        const last = outs[outs.length - 1];
        if (last) {
          collected.push(last);
          setResults([...collected]);
          pushHistory([
            {
              id: job.promptId,
              ts: Date.now(),
              filename: last.filename,
              url: last.url,
              prompt: fullPrompt,
              presetId: preset.id,
              aspectId: aspect.id,
              seconds,
              strength,
              seed: baseSeed + i * 1000,
              width: dims.width,
              height: dims.height,
            },
          ]);
        }
        setDoneCount(i + 1);
      }
      if (!collected.length) throw new Error("İş bitti ama video çıktısı bulunamadı.");
    } catch (e) {
      setRunError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
      setProgress(null);
      abortRef.current = null;
    }
  };

  const canRun = Boolean(file) && prompt.trim().length > 0 && !running;
  const outputDir = outputDirSaved || stats?.outputDir || "";

  const chip = (active: boolean) =>
    `border rounded-lg px-3 py-2 text-left transition ${
      active ? "border-indigo-600 bg-indigo-50" : "hover:bg-gray-50"
    }`;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <header className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Video className="w-6 h-6 text-indigo-600" />
          <div>
            <h1 className="text-xl font-semibold">Video Studio</h1>
            <p className="text-sm text-gray-500">Ürün görselinden yerel LTX-2.3 ile tanıtım videosu</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={ping} className="p-2 rounded-lg border hover:bg-gray-50" title="Bağlantıyı yenile">
            <RefreshCw className={`w-4 h-4 ${checking ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={() => setShowSettings((v) => !v)}
            className="p-2 rounded-lg border hover:bg-gray-50"
            title="Bağlantı ayarları"
          >
            <Settings2 className="w-4 h-4" />
          </button>
        </div>
      </header>

      <div className="mb-4">
        {stats ? (
          <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
            <CheckCircle className="w-4 h-4 shrink-0" />
            <span>
              {stats.deviceName} · VRAM {stats.vramFreeGb}/{stats.vramTotalGb} GB boş · RAM{" "}
              {stats.ramFreeGb}/{stats.ramTotalGb} GB boş · ComfyUI {stats.version}
            </span>
          </div>
        ) : (
          <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <p>{connError ?? "Bağlantı kontrol ediliyor..."}</p>
              <p className="mt-1 text-amber-700">
                ComfyUI'yi <code className="bg-amber-100 px-1 rounded">--enable-cors-header</code> ile
                başlatman gerekir; aksi halde tarayıcı istekleri engeller.
              </p>
            </div>
          </div>
        )}
      </div>

      {showSettings && (
        <div className="mb-4 border rounded-lg p-4 bg-gray-50 space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">ComfyUI adresi</label>
            <div className="flex gap-2">
              <input
                value={urlDraft}
                onChange={(e) => setUrlDraft(e.target.value)}
                placeholder={DEFAULT_COMFY_URL}
                className="flex-1 border rounded-lg px-3 py-2 text-sm"
              />
              <button
                onClick={() => {
                  setComfyUrl(urlDraft);
                  void ping();
                }}
                className="px-4 py-2 text-sm rounded-lg bg-gray-900 text-white"
              >
                Kaydet
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between text-sm text-gray-600 border-t pt-3">
            <span className="flex items-center gap-2">
              <FileJson className="w-4 h-4 text-gray-400" />
              {customTemplate ? "Kendi workflow şablonun" : "Gömülü LTX-2.3 şablonu"} ·{" "}
              {Object.keys(template).length} düğüm
            </span>
            <div className="flex items-center gap-3">
              <label className="text-indigo-600 hover:underline cursor-pointer">
                Değiştir
                <input
                  type="file"
                  accept="application/json,.json"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && void onTemplateFile(e.target.files[0])}
                />
              </label>
              {customTemplate && (
                <button
                  onClick={() => {
                    localStorage.removeItem(TEMPLATE_KEY);
                    setTemplate(DEFAULT_TEMPLATE);
                    setCustomTemplate(false);
                  }}
                  className="text-gray-400 hover:text-gray-700"
                  title="Gömülü şablona dön"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          {templateError && <p className="text-sm text-red-600">{templateError}</p>}
          <div className="border-t pt-3">
            <label className="block text-sm font-medium mb-1">ComfyUI çıktı klasörü</label>
            <div className="flex gap-2">
              <input
                value={outputDirDraft}
                onChange={(e) => setOutputDirDraft(e.target.value)}
                placeholder={stats?.outputDir ?? "örn. C:\\ComfyUI_windows_portable\\ComfyUI\\output"}
                className="flex-1 border rounded-lg px-3 py-2 text-sm font-mono"
              />
              <button
                onClick={() => { setOutputDir(outputDirDraft); setOutputDirSaved(getOutputDir()); }}
                className="px-4 py-2 text-sm rounded-lg bg-gray-900 text-white"
              >
                Kaydet
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {outputDirSaved ? "Elle girildi." : stats?.outputDir ? `ComfyUI'den bulundu: ${stats.outputDir}` : "ComfyUI bildirmedi — klasörü Gezgin'den kopyalayıp yapıştır."}
              {" "}Üretilen videonun dosya yolu bu klasörden hesaplanır.
            </p>
          </div>
        </div>
      )}

      {/* Toplu kuyruk: tum listeler icin video, sayfa acikken sirayla */}
      <BulkVideoQueue
        settings={{
          template, vertical, wishId, outputDir,
          width: dims.width, height: dims.height, frames, fps: FPS,
          imgCompression, withAudio, strengthOverride, negativeOverride,
          summary: [
            aspect.label, quality.label, `${seconds} sn`, `${dims.width}×${dims.height}`,
            vertical === "jewelry" ? `takı: ${(JEWELRY_WISHES.find((w) => w.id === wishId) ?? JEWELRY_WISHES[0]).label}` : "sahne: kayıtlı analizden",
            strengthOverride != null ? `bağlılık ${strengthOverride.toFixed(2)}` : "bağlılık: ön ayar",
            withAudio ? "sesli" : "sessiz",
          ].join(" · "),
        }}
      />

      <div className="grid md:grid-cols-2 gap-6">
        {/* Girdi */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Ürün görseli</label>
            <ProductSourcePicker onPick={(s) => void pickSource(s)} selectedLabel={sourceLabel} />
            {sourceBusy && (
              <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Görsel alınıyor...
              </p>
            )}
            {sourceError && <p className="text-xs text-red-600 mt-2">{sourceError}</p>}
            {previewUrl && (
              <div className="mt-2 flex items-center gap-3 border rounded-lg p-2">
                <img src={previewUrl} alt="" className="h-16 w-16 object-cover rounded" />
                <div className="min-w-0">
                  <p className="text-sm truncate">{sourceLabel}</p>
                  <button
                    onClick={() => {
                      setFile(null);
                      setSourceUrl(null);
                      setSourceLabel(null);
                    }}
                    className="text-xs text-gray-500 hover:text-gray-900"
                  >
                    Kaldır
                  </button>
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-baseline justify-between mb-1">
              <label className="block text-sm font-medium">Ürün tipi</label>
              {shop && (
                <span className="text-xs text-gray-400">
                  {shop.label || shop.shopName} · {vertical}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {PRODUCT_PRESETS.map((p) => (
                <button key={p.id} onClick={() => pickPreset(p.id)} className={chip(presetId === p.id)}>
                  <div className="text-sm font-medium">{p.label}</div>
                  <div className="text-xs text-gray-500">{p.hint}</div>
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Bağlılık {wish?.strength ?? preset.strength} · önerilen {preset.seconds} sn · kendi negatif terimleriyle
            </p>
            {detectNote && <p className="text-xs text-indigo-600 mt-0.5">{detectNote} — degistirebilirsin</p>}
          </div>

          {wish && (
            <div>
              <label className="block text-sm font-medium mb-1">Nasıl bir video istiyoruz?</label>
              <div className="grid grid-cols-2 gap-2">
                {JEWELRY_WISHES.map((w) => (
                  <button key={w.id} onClick={() => applyWish(w.id)} className={chip(wish.id === w.id)}>
                    <div className="text-sm font-medium">{w.label}</div>
                    <div className="text-xs text-gray-500">{w.hint}</div>
                  </button>
                ))}
              </div>
              <input
                value={wishNote}
                onChange={(e) => setWishNote(e.target.value)}
                placeholder="AI ile yazdırırken ek istek (isteğe bağlı): örn. harfler net kalsın"
                className="w-full border rounded-lg px-3 py-2 text-sm mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">
                Seçince hazır prompt sahne tarifine dolar, videoya göre düzenle. İstersen &ldquo;AI ile yaz&rdquo;
                görsele bakıp bu isteğe göre yazar.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">Sahne tarifi</label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={3}
              placeholder="Yumuşak doğal pencere ışığı, sıcak tonlar, sığ alan derinliği"
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
            {!wish && (
              <p className="text-xs text-gray-500 mt-1">
                Ürün tipine göre şu otomatik eklenir: &ldquo;{promptSuffix}&rdquo;
              </p>
            )}

            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              <button
                onClick={() =>
                  void analyzeImage({
                    file,
                    url: sourceUrl,
                    label: sourceLabel,
                    wish: wishTextFor(presetId),
                    refresh: true,
                  })
                }
                disabled={(!file && !sourceUrl) || analyzing}
                className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-indigo-600 text-white disabled:opacity-40"
              >
                {analyzing ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Sparkles className="w-3.5 h-3.5" />
                )}
                {analyzing ? "Analiz ediliyor..." : wish ? "AI ile yaz" : "Yeniden analiz et"}
              </button>
              {analysisCached && !analyzing && (
                <span className="text-xs text-gray-500">Kayıtlı analizden dolduruldu</span>
              )}
              {sceneFamilies.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setPrompt(sceneTextFor(f.id))}
                  className="text-xs px-2.5 py-1.5 rounded-lg border hover:bg-gray-50"
                  title="Hazır sahne tarifi"
                >
                  {f.name}
                </button>
              ))}
            </div>
            {analysisNote && <p className="text-xs text-amber-700 mt-2">{analysisNote}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Oran</label>
              <select
                value={aspectId}
                onChange={(e) => setAspectId(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                {ASPECTS.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label} — {a.hint}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Kalite</label>
              <select
                value={qualityId}
                onChange={(e) => setQualityId(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                {QUALITY_TIERS.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.label} — {q.hint}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Süre</label>
            <div className="grid grid-cols-4 gap-2">
              {DURATIONS.map((s) => (
                <button key={s} onClick={() => setSeconds(s)} className={chip(seconds === s)}>
                  <div className="text-sm font-medium">{s} sn</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Varyasyon</label>
            <div className="grid grid-cols-3 gap-2">
              {[1, 2, 3].map((n) => (
                <button key={n} onClick={() => setVariations(n)} className={chip(variations === n)}>
                  <div className="text-sm font-medium">{n === 1 ? "Tek" : `${n} varyasyon`}</div>
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Farklı seed'lerle üretilir, sırayla işlenir. Süre yaklaşık {variations} katına çıkar.
            </p>
          </div>

          <details className="border rounded-lg px-3 py-2">
            <summary className="text-sm font-medium cursor-pointer">Gelişmiş</summary>
            <div className="space-y-4 mt-3">
              <div>
                <div className="flex items-center justify-between text-sm mb-1">
                  <label className="font-medium">Kaynağa bağlılık</label>
                  <span className="text-gray-500">
                    {strength.toFixed(2)}
                    {strengthOverride == null && " (ön ayar)"}
                  </span>
                </div>
                <input
                  type="range"
                  min={0.5}
                  max={0.98}
                  step={0.01}
                  value={strength}
                  onChange={(e) => setStrengthOverride(Number(e.target.value))}
                  className="w-full"
                />
                <p className="text-xs text-gray-500">
                  Yüksek değer ürünü korur ama hareketi kısar. Kapatılmış alanlarda uydurma nesne
                  çıkıyorsa yükselt.
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between text-sm mb-1">
                  <label className="font-medium">Kaynak netliği</label>
                  <span className="text-gray-500">{imgCompression}</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={40}
                  step={1}
                  value={imgCompression}
                  onChange={(e) => setImgCompression(Number(e.target.value))}
                  className="w-full"
                />
                <p className="text-xs text-gray-500">
                  Düşük = kaynak daha net korunur, yüksek = modele daha fazla serbestlik.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Seed</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={seed}
                    onChange={(e) => setSeed(Number(e.target.value))}
                    className="flex-1 border rounded-lg px-3 py-2 text-sm"
                  />
                  <button
                    onClick={() => setSeedLocked((v) => !v)}
                    className={`px-3 rounded-lg border ${seedLocked ? "bg-indigo-50 border-indigo-600" : ""}`}
                    title={seedLocked ? "Kilitli: her üretimde aynı" : "Serbest: her üretimde rastgele"}
                  >
                    {seedLocked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Negatif prompt</label>
                <textarea
                  value={negative}
                  onChange={(e) => setNegativeOverride(e.target.value)}
                  rows={3}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={withAudio}
                  onChange={(e) => setWithAudio(e.target.checked)}
                  className="rounded"
                />
                Ses ekle <span className="text-xs text-gray-400">(varsayılan sessiz)</span>
              </label>
            </div>
          </details>

          <div className="text-xs text-gray-500 border rounded-lg px-3 py-2 bg-gray-50">
            Üretim {dims.width}×{dims.height} · çıktı {dims.width * 2}×{dims.height * 2} ·{" "}
            {frames} kare ({secondsOf(frames, FPS)} sn @ {FPS}fps)
          </div>

          <button
            onClick={() => void generate()}
            disabled={!canRun}
            className="w-full py-3 rounded-lg bg-indigo-600 text-white font-medium disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />}
            {running ? `Üretiliyor ${doneCount}/${variations}` : "Video üret"}
          </button>
        </div>

        {/* Cikti */}
        <div className="space-y-4">
          <div className="border rounded-lg p-4 min-h-[18rem] flex flex-col">
            {running && (
              <div className="m-auto text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-indigo-600 mb-3" />
                <p className="text-sm font-medium">{progress?.label ?? "Hazırlanıyor"}</p>
                {progress?.ratio != null && (
                  <div className="w-56 h-1.5 bg-gray-200 rounded-full mt-3 mx-auto overflow-hidden">
                    <div
                      className="h-full bg-indigo-600 transition-all"
                      style={{ width: `${Math.round(progress.ratio * 100)}%` }}
                    />
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, "0")} geçti
                </p>
                <button
                  onClick={() => abortRef.current?.abort()}
                  className="text-xs text-gray-500 underline mt-3"
                >
                  İzlemeyi bırak
                </button>
              </div>
            )}

            {!running && results.length > 0 && (
              <div className="space-y-3">
                {results.map((r) => (
                  <div key={r.filename} className="space-y-2">
                    <video src={r.url} controls loop className="w-full rounded-lg bg-black" />
                    <a
                      href={r.url}
                      download={r.filename}
                      className="inline-flex items-center gap-2 text-sm px-3 py-1.5 border rounded-lg hover:bg-gray-50"
                    >
                      <Download className="w-4 h-4" />
                      {r.filename}
                    </a>
                    <ComfyFileLink url={r.url} filename={r.filename} subfolder={r.subfolder} outputDir={outputDir} />
                    <AttachVideoToDraft videoUrl={r.url} preferIds={sourceDraftIds} />
                  </div>
                ))}
              </div>
            )}

            {!running && !results.length && !runError && (
              <div className="m-auto text-center text-gray-400">
                <Video className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm">Üretilen video burada görünecek</p>
              </div>
            )}

            {runError && (
              <div className="mt-auto flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <p className="break-words">{runError}</p>
              </div>
            )}
          </div>

          {/* Gecmis */}
          {history.length > 0 && (
            <div className="border rounded-lg">
              <div className="flex items-center justify-between px-3 py-2 border-b">
                <span className="flex items-center gap-2 text-sm font-medium">
                  <History className="w-4 h-4 text-gray-400" />
                  Geçmiş ({history.length})
                </span>
                <button
                  onClick={() => {
                    localStorage.removeItem(HISTORY_KEY);
                    setHistory([]);
                  }}
                  className="text-gray-400 hover:text-gray-700"
                  title="Geçmişi temizle"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <ul className="max-h-72 overflow-y-auto divide-y">
                {history.map((h) => (
                  <li key={h.id} className="px-3 py-2 text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <a
                        href={h.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-indigo-600 hover:underline truncate"
                      >
                        {h.filename}
                      </a>
                      <button
                        onClick={() => {
                          setPresetId(h.presetId);
                          setAspectId(h.aspectId);
                          setSeconds(h.seconds);
                          setStrengthOverride(h.strength);
                          setSeed(h.seed);
                          setSeedLocked(true);
                        }}
                        className="text-gray-500 hover:text-gray-900 shrink-0"
                        title="Bu ayarları geri yükle"
                      >
                        Ayarları al
                      </button>
                    </div>
                    <p className="text-gray-500 mt-0.5">
                      {h.width}×{h.height} · {h.seconds} sn · bağlılık {h.strength} · seed {h.seed}
                    </p>
                    <p className="text-gray-400 truncate">{h.prompt}</p>
                    <div className="mt-1"><ComfyFileLink url={h.url} outputDir={outputDir} /></div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
